import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

/**
 * BRK-1856: submit-worksheet confirmation. Shows how many items the submission
 * will change and the resulting completeness across editable sections
 * (partial submit is allowed — the PDF is watermarked "Incomplete" later), and
 * captures an optional version note. Backend rejections (403 for a
 * non-editable section, validation) surface inline.
 */

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (note: string) => Promise<void>;
  changeCount: number;
  scored: number;
  total: number;
};

const SubmitWorksheetDialog: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  changeCount,
  scored,
  total,
}) => {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNote('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(note);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to submit worksheet');
    } finally {
      setSubmitting(false);
    }
  };

  const incomplete = total - scored;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Submit MDS worksheet</DialogTitle>
      <DialogContent>
        <Stack
          gap={1.5}
          sx={{ mt: 1 }}
        >
          <Typography variant="body2">
            {changeCount} change{changeCount === 1 ? '' : 's'} will be submitted as a new version.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {scored} of {total} editable item{total === 1 ? '' : 's'} scored
            {incomplete > 0
              ? ` — ${incomplete} still blank (the printed worksheet will be marked Incomplete).`
              : '.'}
          </Typography>
          <TextField
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            inputProps={{ maxLength: 2000 }}
          />
          {error && <Typography color="error">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || changeCount === 0}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitWorksheetDialog;
